<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, useAttrs } from 'vue'

defineOptions({
  name: 'CommonImage',
  inheritAttrs: false,
})

// 主图不可用、未传 src 或超时后的默认兜底图。
const DEFAULT_FALLBACK_IMAGE =
  'https://resources1.interface003.com/web30/2026/07/07/dfb37400-c561-4a6f-8718-2ac0e0d9eca6.svg'
// 尚未开始加载时使用 1×1 透明 GIF，避免浏览器提前请求真实图片。
const LAZY_PLACEHOLDER_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
const OSS_IMAGE_FORMAT = 'webp'
// 仅对明确支持的源格式追加 OSS 转码参数，避免破坏 SVG、接口地址或未知资源。
const OSS_SUPPORTED_SOURCE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'gif',
  'webp',
  'tif',
  'tiff',
  'heic',
  'avif',
])

const props = withDefaults(
  defineProps<{
    /** 主图地址；会去除首尾空白，并在符合 OSS 格式要求时自动追加 WebP 转码参数。 */
    src?: string | null
    /** 主图的替代文本；同时应用于兜底图，供读屏软件和图片加载失败场景使用。 */
    alt?: string
    /** 外层图片容器的宽度；纯数字会转换为 px，其他合法 CSS 单位（如 `100%`）保持不变。 */
    width?: string | number
    /** 外层图片容器的高度；纯数字会转换为 px，其他合法 CSS 单位（如 `auto`）保持不变。 */
    height?: string | number
    /**
     * 加载时机。`lazy` 会在图片进入视区（含预加载边距）后请求资源，
     * `eager` 会在组件创建时立即请求；默认使用 `lazy` 以降低首屏非关键图片的网络开销。
     */
    loading?: 'eager' | 'lazy'
    /** 浏览器图片请求优先级；首屏关键图片可设为 `high`，非关键图片可设为 `low`。 */
    fetchPriority?: 'high' | 'low' | 'auto'
    /**
     * 懒加载的视区外预加载距离，单位为 px。未传时使用应用全局的
     * `hydrateOnVisibleRootMargin` 配置；数值越大，图片越早开始请求。
     */
    lazyMargin?: number
    /**
     * 主图默认的 object-fit 策略。若调用方的 class 包含 `object-*`，
     * 则 class 的策略优先，用于需要按页面布局覆盖默认展示方式的场景。
     */
    objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
    /**
     * 主图无地址、加载失败或超时后的兜底图片地址；传入 `false` 时不展示业务兜底图，
     * 仅使用透明占位图来维持原有布局。
     */
    fallbackSrc?: string | boolean
    /** 兜底图相对容器的最大缩放百分比，最终会被限制在 0–100 之间，默认 50。 */
    fallbackScale?: number
    /**
     * 图片请求超时时间，单位为 ms。超过该时间仍未成功加载时会切换到兜底状态并触发 `error`；
     * 传入小于或等于 0 的值可关闭超时检测。
     */
    errorTimeoutMs?: number
  }>(),
  {
    src: '',
    alt: '',
    width: undefined,
    height: undefined,
    loading: 'lazy',
    fetchPriority: 'auto',
    objectFit: 'cover',
    fallbackSrc: DEFAULT_FALLBACK_IMAGE,
    fallbackScale: 50,
    errorTimeoutMs: 20000,
  },
)

// 事件保留原生 Event，方便调用方根据实际图片元素的加载结果继续处理。
const emit = defineEmits<{
  error: [event: Event]
  load: [event: Event]
}>()

// 获取全局的图片可视区预加载边距配置。
const appStore = useAppStore()
// 接收并透传容器上的 class、data-*、aria-* 等非受控属性。
const attrs = useAttrs()
// 加载状态分别记录“资源失败”和“资源成功”，以区分初始占位、正常图片与兜底图。
const hasLoadError = ref(false)
const hasLoaded = ref(false)
const mainImageRef = ref<HTMLImageElement | null>(null)
// 非 lazy 图片立即加载；lazy 图片由 IntersectionObserver 进入可视区后切换为 true。
const shouldLoad = ref(props.loading !== 'lazy')
let errorTimeoutTimer: ReturnType<typeof setTimeout> | null = null
let imageObserver: IntersectionObserver | null = null

// 消除空白与非字符串值，作为后续图片加载和转码判断的唯一来源。
const normalizedSrc = computed(() => (typeof props.src === 'string' ? props.src.trim() : ''))
const hasSource = computed(() => Boolean(normalizedSrc.value))
// 为可由 OSS 处理的位图格式追加 WebP 转码；保留原有查询参数和 hash，且跳过已处理的地址。
const optimizedSrc = computed(() => {
  const source = normalizedSrc.value
  if (!source) {
    return source
  }

  const hashIndex = source.indexOf('#')
  const hash = hashIndex >= 0 ? source.slice(hashIndex) : ''
  const sourceWithoutHash = hashIndex >= 0 ? source.slice(0, hashIndex) : source
  const queryIndex = sourceWithoutHash.indexOf('?')
  const pathname = queryIndex >= 0 ? sourceWithoutHash.slice(0, queryIndex) : sourceWithoutHash
  const sourceExtension = pathname.match(/\.([^.]+)$/)?.[1]?.toLowerCase()

  if (/\.[^./]+_[^/?#]+$/.test(pathname) || !sourceExtension || !OSS_SUPPORTED_SOURCE_EXTENSIONS.has(sourceExtension)) {
    return source
  }

  const separator = queryIndex >= 0 ? '&' : '?'
  return `${sourceWithoutHash}${separator}x-oss-process=image/format,${OSS_IMAGE_FORMAT}${hash}`
})
// 将兜底图缩放范围限制在 0%–100%，防止错误配置撑破图片容器。
const safeFallbackScale = computed(() => Math.min(Math.max(props.fallbackScale, 0), 100))
// 原生 img 的 width/height 属性和 CSS 尺寸语义不同；数字统一补 px 保证容器尺寸正确。
const normalizeImageSize = (value?: string | number) => {
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value))) {
    return `${value}px`
  }

  return value
}
// 容器尺寸使用标准 CSS 值，供外层容器与内部主图保持一致。
const imageWidth = computed(() => normalizeImageSize(props.width))
const imageHeight = computed(() => normalizeImageSize(props.height))
// 兼容调用方通过 Tailwind 尺寸类控制大小的场景，而非仅依赖 width/height props。
const hasSizeClass = computed(() => {
  if (typeof attrs.class !== 'string') {
    return false
  }

  return /(?:^|\s)(?:size|w|h)-(?:\[|auto|full|screen|\d)/.test(attrs.class)
})
const shouldFillContainer = computed(() => Boolean(imageWidth.value || imageHeight.value || hasSizeClass.value))
// 调用方传入的 object-* 类优先级高于 objectFit prop，便于在模板中按状态动态覆盖。
const imageObjectFitClass = computed(() => {
  if (typeof attrs.class !== 'string') {
    return ''
  }

  return attrs.class.split(/\s+/).find((className) => className.startsWith('object-')) || ''
})
// 开始加载后无地址或主图失败时进入明确的兜底状态。
const showFallback = computed(() => shouldLoad.value && (!hasSource.value || hasLoadError.value))
// lazy 图片在真实资源成功前也展示兜底图，避免容器在首屏出现空白。
const showFallbackImage = computed(() => showFallback.value || (props.loading === 'lazy' && !hasLoaded.value))
// 主图只在有地址、未失败且（lazy 时）实际加载完成后显示，防止占位与主图闪烁重叠。
const showMainImage = computed(
  () => hasSource.value && !hasLoadError.value && (props.loading !== 'lazy' || hasLoaded.value),
)
const fallbackImageClass = computed(() => (showFallbackImage.value ? 'is-visible' : 'is-hidden'))
// `fallbackSrc=false` 表示不展示业务兜底图，仅保持透明占位。
const fallbackImageSrc = computed(() => {
  if (props.fallbackSrc === false) {
    return LAZY_PLACEHOLDER_IMAGE
  }

  return typeof props.fallbackSrc === 'string' ? props.fallbackSrc : DEFAULT_FALLBACK_IMAGE
})

// 处理浏览器缓存命中：图片可能在事件绑定前已完成加载，需主动读取 complete/naturalWidth 同步状态。
const syncLoadedState = () => {
  if (!import.meta.client || !shouldLoad.value) {
    return
  }

  const imageEl = mainImageRef.value

  if (!imageEl || !hasSource.value) {
    return
  }

  if (imageEl.complete && imageEl.naturalWidth > 0) {
    clearErrorTimeout()
    hasLoadError.value = false
    hasLoaded.value = true
  }
}

function clearErrorTimeout() {
  if (!errorTimeoutTimer) {
    return
  }

  clearTimeout(errorTimeoutTimer)
  errorTimeoutTimer = null
}

// 网络请求长时间无结果也按失败处理，避免页面一直停留在透明占位状态。
function startErrorTimeout() {
  clearErrorTimeout()

  if (!import.meta.client || !shouldLoad.value || props.errorTimeoutMs <= 0 || !normalizedSrc.value) {
    return
  }

  errorTimeoutTimer = setTimeout(() => {
    syncLoadedState()

    if (hasLoadError.value || hasLoaded.value) {
      return
    }

    hasLoadError.value = true
    emit('error', new Event('error'))
  }, props.errorTimeoutMs)
}

watch(
  normalizedSrc,
  () => {
    // 每次图片地址变更都重新走完整的加载、超时和缓存命中检测流程。
    hasLoadError.value = false
    hasLoaded.value = false
    startErrorTimeout()

    if (import.meta.client) {
      void nextTick(syncLoadedState)
    }
  },
  { immediate: true },
)

// 可视区观察器触发后启动真实请求，并补做一次浏览器缓存命中检测。
watch(shouldLoad, (value) => {
  if (!value) {
    return
  }

  startErrorTimeout()
  if (import.meta.client) {
    void nextTick(syncLoadedState)
  }
})

// 兜底图本身加载失败时仍向外通知，供调用方埋点或降级处理。
const handleFallbackError = (event: Event) => {
  emit('error', event)
}

const handleImageError = (event: Event) => {
  clearErrorTimeout()
  hasLoadError.value = true
  hasLoaded.value = false
  emit('error', event)
}

const handleImageLoad = (event: Event) => {
  if (!shouldLoad.value || !hasSource.value) {
    return
  }

  clearErrorTimeout()
  hasLoadError.value = false
  hasLoaded.value = true
  emit('load', event)
}

onMounted(() => {
  // 使用应用级 rootMargin 提前触发请求；不支持 IntersectionObserver 时降级为立即加载。
  if (props.loading === 'lazy' && 'IntersectionObserver' in window && mainImageRef.value) {
    imageObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        shouldLoad.value = true
        imageObserver?.disconnect()
        imageObserver = null
      },
      {
        rootMargin: `${props.lazyMargin ?? appStore.hydrateOnVisibleRootMargin}px 0px`,
        threshold: 0,
      },
    )
    imageObserver.observe(mainImageRef.value)
  } else {
    shouldLoad.value = true
  }

  syncLoadedState()
})

onBeforeUnmount(() => {
  // 清除计时器并断开观察器，避免组件卸载后继续更新响应式状态。
  clearErrorTimeout()
  imageObserver?.disconnect()
  imageObserver = null
})
</script>

<template>
  <div
    v-bind="$attrs"
    class="common-image"
    :class="{
      'common-image--sized': shouldFillContainer,
      'common-image--fallback': showFallback,
    }"
    :style="{ width: imageWidth, height: imageHeight }"
  >
    <img
      :src="fallbackImageSrc"
      :alt="alt"
      data-allow-mismatch="class"
      class="image-layer image-fallback"
      :class="fallbackImageClass === 'is-visible' ? 'visible' : 'invisible'"
      :style="{ width: `${safeFallbackScale}%`, maxHeight: `${safeFallbackScale}%` }"
      aria-hidden="true"
      @error="handleFallbackError"
    />
    <img
      ref="mainImageRef"
      :src="shouldLoad ? optimizedSrc : LAZY_PLACEHOLDER_IMAGE"
      :alt="alt"
      data-allow-mismatch="class"
      :width="width"
      :height="height"
      class="image-layer main-image"
      :class="[
        imageObjectFitClass,
        {
          visible: showMainImage,
          invisible: !showMainImage,
          'object-cover': props.objectFit === 'cover' && !imageObjectFitClass,
          'object-contain': props.objectFit === 'contain' && !imageObjectFitClass,
          'object-fill': props.objectFit === 'fill' && !imageObjectFitClass,
          'object-none': props.objectFit === 'none' && !imageObjectFitClass,
          'object-scale-down': props.objectFit === 'scale-down' && !imageObjectFitClass,
        },
      ]"
      :style="{
        objectFit,
      }"
      :loading="loading"
      :fetchpriority="fetchPriority"
      @error="handleImageError"
      @load="handleImageLoad"
    />
  </div>
</template>

<style scoped>
@layer components {
  :where(.common-image) {
    position: relative;
  }
}

:where(.common-image) {
  display: inline-block;
  overflow: hidden;
  vertical-align: middle;
}

.common-image--fallback {
  background-color: #f2f4f5;
}

.image-layer {
  display: block;
}

.image-fallback {
  position: absolute;
  inset: 0;
  z-index: 0;
  margin: auto;
  object-fit: contain;
  pointer-events: none;
  animation: fallback-fade-in 200ms ease 300ms both;
}

@keyframes fallback-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.main-image {
  position: relative;
  z-index: 1;
  width: auto;
  height: auto;
  max-width: 100%;
}

.common-image--sized .main-image {
  width: 100%;
  height: 100%;
}
</style>
